// here we can implement analytics events triggering functions

import { Logger } from './app/services/logger.service'
import { TBookId } from '@shared/book/interfaces'
import { TStepId } from './app/pages/order/interfaces'
import { OrderData, IAddressBillingData } from '@functions/interfaces';
import { TGiftOrderGenerated, TGiftOrderCreated } from '@functions/components/gift/interfaces';
import { environment } from 'src/environments/environment';

const triggerCheck = <FN, ARGS extends any[]>(log: Logger, fn: FN, ...args: ARGS) => {
  if (typeof fn === 'function') {
    fn(...args);
  } else {
    const level = environment.production
      ? 'error'
      : 'info';

    log[level](`trigger doesn't exist, arguments:`, ...args);
  }
}

const gtagTriggerLog = new Logger('gtagTrigger');
export const gtagTrigger: Gtag.Gtag = (...args: any[]) => 
  triggerCheck(gtagTriggerLog, window.gtag, ...args);

const fbqTriggerLog = new Logger('fbqTrigger');
export const fbqTrigger: facebook.Pixel.Event = (...args: any[]) =>
  triggerCheck(fbqTriggerLog, window.fbq, ...args);

const _ltTriggerLog = new Logger('_ltTrigger');
export const _ltTrigger = (...args: any) => 
  triggerCheck(_ltTriggerLog, window._lt, ...args);

const ytagTriggerLog = new Logger('ytagTrigger');
export const ytagTrigger = (...args: any) =>
  triggerCheck(ytagTriggerLog, window.ytag, ...args);

const pintrkTriggerLog = new Logger('pintrkTrigger');
export const pintrkTrigger = (...args: any) =>
  triggerCheck(pintrkTriggerLog, window.pintrk, ...args);

// * Google Anlytics section

/**
 * set of google analytics events we are waiting for
 */
export enum GAEvents {
  thankyou = 'thankyou',
}

export const GAGetEvents = {
  getBookPreview: (bookId: TBookId) => `book_preview_${bookId}`,
  getAddToCart: (bookId: TBookId) => `add_to_cart_${bookId}`,
  getCheckoutStep: (stepId: TStepId) => `checkout_step_${stepId}`,
}

/**
 * triggers Google Analytics event by
 * ! ga('track', `event`)
 * @param event - one of `FBEvents` values
 */
export const GATrack = (event: GAEvents | string) =>
  gtagTrigger('event', event, {
    event_category: 'Default',
    send_to: 'G-90W20H1E75',
  });

// * Facebook Pixel section

/**
 * set of facebook pixel events we are waiting for
 */
export enum FBEvents {
  viewContent = 'ViewContent',
  addToCart = 'AddToCart',
  initiateCheckout = 'InitiateCheckout',
  purchase = 'Purchase',
}

/**
 * triggers Facebook Pixel event by
 * fbq('track', `event`)
 * @param event - one of `FBEvents` values
 */
export const FBTrack = (
  event: FBEvents,
  parameters?:
    | facebook.Pixel.ViewContentParameters
    | facebook.Pixel.SearchParameters
    | facebook.Pixel.AddToCartParameters
    | facebook.Pixel.AddToWishlistParameters
    | facebook.Pixel.InitiateCheckoutParameters
    | facebook.Pixel.AddPaymentInfoParameters
    | facebook.Pixel.PurchaseParameters
    | facebook.Pixel.LeadParameters
    | facebook.Pixel.CompleteRegistrationParameters,
) => 
  fbqTrigger('track', event, parameters);


const sendConversionEvent = (orderData: {
  addressBillingData: IAddressBillingData,
  orderId: string,
}) => {
  // FBTrack(FBEvents.purchase, {
  //   value: 3900,
  //   currency: 'JPY',
  // });
  // * old approach
  window.enhanced_conversion_data = {
    email: orderData.addressBillingData.emailAddress,
    phone_number: `81${orderData.addressBillingData.phone.replace(/(^0|\D)/g, '')}`,
    first_name: orderData.addressBillingData.givenName,
    last_name: orderData.addressBillingData.familyName,
    home_address: {
      region: orderData.addressBillingData.addressLine1,
      postal_code: orderData.addressBillingData.postalCode.replace(/\D/g, ''),
      country: 'JP',
    },
  };
  // * new approach
  gtagTrigger('set', 'user_data', {
    email: orderData.addressBillingData.emailAddress,
    phone_number: `+81${orderData.addressBillingData.phone.replace(/(^0|\D)/g, '')}`,
    address: {
      first_name: orderData.addressBillingData.givenName,
      last_name: orderData.addressBillingData.familyName,
      region: orderData.addressBillingData.addressLine1,
      postal_code: orderData.addressBillingData.postalCode.replace(/\D/g, ''),
      country: 'JP',
    }
  });
  GATrack(GAEvents.thankyou);
  gtagTrigger('event', 'conversion', {
    send_to: 'AW-467679936/jdnNCJeZiIcDEMD1gN8B',
    transaction_id: orderData.orderId,
  });
  _ltTrigger('send', 'cv', {
    type: 'Conversion'
  }, ['1011300d-ebc2-41d8-bbab-99cb084c7a71']);
  ytagTrigger({
    type: 'yss_conversion',
    config: {
      yahoo_conversion_id: '1001220847',
      yahoo_conversion_label: 'z9LKCOvDo4ADENjZ6Z0o',
      yahoo_conversion_value: '4200'
    }
  });
  ytagTrigger({
    type: 'yjad_conversion',
    config: {
      yahoo_ydn_conv_io: 'ZoUdsP-7teY_QFIYSJpYGg..',
      yahoo_ydn_conv_label: '3J9CPRTH0PY7E9TLHEH857474',
      yahoo_ydn_conv_transaction_id: '',
      yahoo_ydn_conv_value: '4200',
      yahoo_email: orderData.addressBillingData.emailAddress,
      yahoo_phone_number: orderData.addressBillingData.phone.replace(/\D/g, ''),  
    }
  });

  const tempErr = console.error;
  console.error = console.warn;
  pintrkTrigger('load', '2612978562674', { em: orderData.addressBillingData.emailAddress });
  pintrkTrigger('page');
  console.error = tempErr;

  pintrkTrigger('track', 'checkout', {
    value: 4200,
    currency: 'JPY',
    order_id: orderData.orderId,
  });
}

export const sendConversionEventByOrder = (orderData: OrderData) => {
  const {
    addressBillingData,
    orderId,
  } = orderData;

  sendConversionEvent({
    addressBillingData,
    orderId,
  });
}

export const sendConversionEventByGiftOrder = (
  orderId: string,
  giftOrder: TGiftOrderCreated | TGiftOrderGenerated,
) => {
  const {
    addressBillingData,
  } = giftOrder;

  sendConversionEvent({
    addressBillingData,
    orderId,
  });
}
